/**
 * "Failed" is seven things on WhatsApp, and this is where they stay seven.
 * Slice 12, decision 1.
 *
 * `CampaignNotDelivered` beside this file does the same job for email and is
 * worth reading first: the argument is identical, the vocabulary shares not one
 * word, and the difference is the reason both files exist rather than one
 * parameterised file. Amazon tells you *what kind* of failure it was — a
 * permanent bounce, a transient one, a rejection, a rendering failure — in the
 * event type and the bounce type, so the five email outcomes are five different
 * shapes of notification. **Meta tells you nothing but `failed`.** An
 * unregistered number, a blown rate limit, a paused template, a marketing
 * opt-out, an expired 24-hour window and a dead access token all arrive as the
 * same word, and the numeric error code beside it is the entire diagnosis.
 *
 * So the split here is a *classification*, not a transcription, and that is
 * exactly why nothing in this file performs it. `classifyWaFailureCode` in
 * `@wend/shared` owns the 38-code table, `waFailureBreakdown` does the
 * counting, and this component receives the seven totals already decided. A
 * screen that mapped codes to buckets itself would be a second opinion about
 * which numbers get suppressed, and the two opinions would diverge on the day
 * somebody adds a code to one of them.
 *
 * **Every sentence is the contract's too**, which is where this file differs
 * from `CampaignNotDelivered`. There, the screen owns the "what can you do
 * about it" sentence because the wire has no place to carry one. Here
 * `WA_FAILURE_CLASS_MESSAGES` exists and is deliberately worded as a repair
 * rather than a disclaimer, so re-writing those sentences in a component would
 * mean the API's own explanation of a suppression and the screen's explanation
 * of the same suppression could say different things. What this file owns is
 * three things the contract has no opinion about: the chip's label, its tone,
 * and the order the rows appear in.
 *
 * **The order is the argument of the panel.** Rows run from "nothing you send
 * will work until you fix this" down to "there is nothing to do", the same rule
 * `NOT_DELIVERED_OUTCOMES` and `SKIP_REASON_ORDER` follow, because the first row
 * is the one people actually read. `account` leads: it is not about these
 * recipients at all — an expired token or a blocked WABA fails every message on
 * the channel, so a non-zero there makes every other row on the panel a
 * consequence rather than a cause. `unclassified` is last because it is the
 * residual bucket and a residual bucket at the top would read as the headline
 * finding.
 *
 * **`transient` is drawn `info`, and it is the single most important tone on
 * this screen.** A rate limit is per-recipient on WhatsApp — Meta throttles the
 * *pair*, not the account — so it arrives looking exactly like a fact about that
 * phone number when it is a fact about the pace of the send. Painted the same
 * red as an unreachable number, it is what makes somebody delete a real
 * customer's working number from their list to "clean" it. The contract's
 * sentence for that class says *do not clean these off the list* in those words,
 * and the tone here is chosen so the colour does not contradict the sentence.
 *
 * **Rows with nobody in them are dropped**, the same rule as every other
 * breakdown in this product: seven permanent zeros teach a person to skip the
 * section on the campaign where one of them is not zero. When every one of them
 * is empty the panel says so in a sentence — and unlike the email side, that
 * sentence needs no "on this channel nothing is measured" escape hatch, because
 * since slice 12 WhatsApp *is* measured. `isBounceMeasured` is what keeps the
 * SES-shaped panel off this channel; nothing keeps this one off email, because
 * nothing renders it there.
 *
 * **Since slice 14 these rows have a second reader, and that is why
 * `WA_FAILURE_ROWS` is exported.** The range screen at `/analytics` prints the
 * same seven labels, tones and order — with the contract's own sentences beside
 * them — over a whole period. It does not reuse this component, because the
 * empty state above speaks about one campaign and a period holds many. The three
 * things this file owns are still owned in one place; they are now read in two.
 *
 * **These seven do not add up to a fifth figure and there is no `total`.** The
 * `failed` counter above them is `messages.status = 'failed'` — a count of
 * messages — while these count the classified reasons behind those messages'
 * events. In the ordinary case they agree; in the case that matters they do not,
 * because a message can honestly carry two failures at two different times, and
 * a person handed one "412 failed" number cleans their list, whereas a person
 * handed "1 unreachable, 411 transient" sends the campaign again tomorrow and
 * keeps 411 customers. That difference is the entire value of this panel.
 *
 * **Slice 17 shortened the paragraph above the rows and touched nothing else
 * here.** The seven sentences beside the rows are `WA_FAILURE_CLASS_MESSAGES`,
 * which is the contract's and not this screen's to cut — the paragraph above
 * explains why, and it applies to a shortening exactly as it applies to a
 * rewrite. What the intro lost was the clause explaining *why* the rows are kept
 * apart, which is an argument for the panel rather than a fact about this
 * campaign, and which this header has carried all along. What it kept is the
 * sentence a reader can draw a false conclusion without: that all seven arrive
 * as one word, and that they do not add up.
 */
import {
  WA_FAILURE_CLASS_MESSAGES,
  type WaFailureBreakdown,
  type WaFailureClass,
} from '@wend/shared';
import { Badge, Kicker, type BadgeTone } from '@wend/ui';

import { formatCount } from '../../lib/format';

export interface WaFailureRow {
  readonly key: WaFailureClass;
  /** Two or three words, for a chip. */
  readonly label: string;
  readonly tone: BadgeTone;
}

/**
 * The seven, ordered by what the reader has to do about them.
 *
 * Typed as `WaFailureClass` per row rather than as a free string, so a class
 * added to `WA_FAILURE_CLASS_VALUES` in the contract without a row here is a
 * compile error at the point where the missing key is looked up — the panel
 * cannot silently stop showing a category of failure.
 *
 * The labels avoid Meta's own words on purpose. "Re-engagement message" and
 * "pair rate limit" are precise and mean nothing to somebody who has never read
 * the Cloud API docs; "the 24-hour window closed" and "sent too fast" are what
 * actually happened.
 */
export const WA_FAILURE_ROWS: readonly WaFailureRow[] = [
  { key: 'account', label: 'Your WhatsApp account', tone: 'bad' },
  { key: 'template', label: 'Template refused', tone: 'warn' },
  { key: 'request', label: "Wend's mistake", tone: 'warn' },
  { key: 'policy', label: "WhatsApp's messaging rules", tone: 'warn' },
  { key: 'unreachable', label: 'Not on WhatsApp', tone: 'bad' },
  { key: 'transient', label: 'Temporary — send again later', tone: 'info' },
  { key: 'unclassified', label: 'Reason not recognised', tone: 'neutral' },
];

export interface CampaignWaFailuresProps {
  breakdown: WaFailureBreakdown;
  /**
   * Messages whose final state is `failed`, from `messages` rather than from
   * `delivery_events`.
   *
   * Passed in so the empty state can tell apart the two situations that both
   * produce seven zeros: nothing failed at all, and something failed but no
   * event carrying a reason has been classified for it. Those are different
   * facts and only one of them is reassuring.
   */
  failed: number;
}

export function CampaignWaFailures({ breakdown, failed }: CampaignWaFailuresProps) {
  const rows = WA_FAILURE_ROWS.filter((row) => breakdown[row.key] > 0);

  return (
    <div className="flex flex-col gap-3 pt-2">
      <Kicker rule>
        Why messages did not arrive
      </Kicker>

      {rows.length === 0 ? (
        <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
          {failed === 0
            ? 'WhatsApp has not reported a failure on this campaign. That is a read of the statuses on file, not a promise about the messages still waiting for one.'
            : 'WhatsApp reported a failure but sent no reason Wend could read with it, so there is nothing to break down. Nobody has been suppressed on the strength of it.'}
        </p>
      ) : (
        <>
          <p className="max-w-prose text-[0.88rem] leading-relaxed text-muted">
            WhatsApp reports every one of these as the single word "failed", and only the error code
            beside it tells a number that will never work from a message sent a second too fast.
            They do not total: one message refused twice for two reasons is honestly on two lines.
          </p>

          <ul className="flex flex-col">
            {rows.map((row) => (
              <li
                key={row.key}
                className="flex flex-col gap-2 border-t border-line py-3.5 first:border-t-0 first:pt-0"
              >
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <span className="text-[1.05rem] font-semibold tabular-nums text-text">
                    {formatCount(breakdown[row.key])}
                  </span>
                  <Badge tone={row.tone}>{row.label}</Badge>
                </div>
                <p className="max-w-prose text-[0.88rem] leading-relaxed text-muted">
                  {WA_FAILURE_CLASS_MESSAGES[row.key]}
                </p>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
